import api from './api';

/**
 * Each person's recent searches, per search box (server/routes/recentSearches.js).
 *
 * Kept on the server so the website and the app show the same list. A copy is
 * held in memory per person and search box so the list opens instantly; it is
 * refreshed from the server when the box is opened, at most every few seconds.
 * Logging out reloads the page, which drops the copy.
 */

export const MAX_RECENT_SEARCHES = 4;
const REFRESH_MS = 15 * 1000;

const lists = new Map();      // key -> terms
const fetchedAt = new Map();  // key -> time of the last read from the server
const listeners = new Map();  // key -> Set of callbacks

const currentUserId = () => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        return String(user?.id || user?._id || '');
    } catch {
        return '';
    }
};

const keyOf = (scope) => `${currentUserId()}|${scope}`;

const cleanTerm = (value) => String(value ?? '').trim().replace(/\s+/g, ' ').slice(0, 100);

const put = (scope, terms) => {
    const key = keyOf(scope);
    lists.set(key, terms);
    (listeners.get(key) || []).forEach((fn) => fn(terms));
};

export const peekRecentSearches = (scope) => lists.get(keyOf(scope)) || [];

export const subscribeRecentSearches = (scope, fn) => {
    const key = keyOf(scope);
    if (!listeners.has(key)) listeners.set(key, new Set());
    listeners.get(key).add(fn);
    return () => listeners.get(key)?.delete(fn);
};

/** Reads the list from the server, unless it was read moments ago. Never throws. */
export const loadRecentSearches = async (scope) => {
    const key = keyOf(scope);
    if (Date.now() - (fetchedAt.get(key) || 0) < REFRESH_MS) return peekRecentSearches(scope);
    fetchedAt.set(key, Date.now());
    try {
        const { data } = await api.get('/recent-searches', { params: { scope } });
        put(scope, data?.terms || []);
    } catch {
        // No list is fine: the search box works as before.
        fetchedAt.delete(key);
    }
    return peekRecentSearches(scope);
};

/** Saves a search: on top of the list straight away, then on the server. */
export const saveRecentSearch = async (scope, value) => {
    const term = cleanTerm(value);
    if (!term) return;
    const lower = term.toLowerCase();
    put(scope, [term, ...peekRecentSearches(scope).filter((t) => t.toLowerCase() !== lower)].slice(0, MAX_RECENT_SEARCHES));
    try {
        const { data } = await api.post('/recent-searches', { scope, term });
        if (Array.isArray(data?.terms)) put(scope, data.terms);
    } catch {
        // Kept for this page only; the next save or refresh catches up.
    }
};

/** Removes one search, or clears the list when no term is given. */
export const removeRecentSearch = async (scope, value) => {
    const term = cleanTerm(value);
    const lower = term.toLowerCase();
    put(scope, term ? peekRecentSearches(scope).filter((t) => t.toLowerCase() !== lower) : []);
    try {
        const { data } = await api.delete('/recent-searches', { params: term ? { scope, term } : { scope } });
        if (Array.isArray(data?.terms)) put(scope, data.terms);
    } catch {
        // Gone from this page; the server copy goes on the next try.
    }
};
